import { useCallback, useEffect, useRef, useState } from 'react';
import { NotebookPen, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_MS = 30000;

// Shared notes pad + AI note-taking (host toggle, OFF by default). Each participant records ITS OWN mic in 30-s chunks
// while AI notes are on; the server transcribes, stitches a speaker-labelled transcript and posts the notes to the chat.
export default function MeetingNotesPanel({ callId, token, isHost, getMicTrack, open, onClose, onStatus }) {
  const [text, setText] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [meta, setMeta] = useState({});
  const typingRef = useRef(0);
  const recRef = useRef(null);
  const hdr = { Authorization: `Bearer ${token}` };
  const live = callId && callId !== 'pending';

  const load = useCallback(async () => {
    if (!live) return;
    try {
      const r = await fetch(`${API}/api/calls/${callId}/notes`, { headers: hdr });
      if (!r.ok) return;
      const d = await r.json();
      setEnabled(!!d.transcription_enabled); setMeta({ by: d.updated_by, at: d.updated_at });
      if (Date.now() - typingRef.current > 2500) setText(d.text || '');
    } catch (e) { /* offline */ }
  }, [callId, live]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const h = (e) => { if (e.detail && e.detail.call_id === callId) { setEnabled(!!e.detail.enabled); toast.info(`${e.detail.by_name || 'The host'} turned AI notes ${e.detail.enabled ? 'on' : 'off'}`); } };
    window.addEventListener('bbc:call-transcription', h); return () => window.removeEventListener('bbc:call-transcription', h);
  }, [callId]);
  useEffect(() => { if (onStatus) onStatus(enabled); }, [enabled, onStatus]);

  // Native Android call screen records the mic itself (APK ≥ 2.8.5); the page only relays the switch.
  useEffect(() => {
    const b = window.AndroidBridge;
    if (b && typeof b.setTranscription === 'function') { try { b.setTranscription(!!enabled, callId || '', API, token || ''); } catch (e) { /* older app */ } }
  }, [enabled, callId, token]);

  // Browser recorder: 30-s standalone chunks of my own microphone.
  useEffect(() => {
    if (!enabled || !live || window.AndroidBridge || !window.MediaRecorder) return undefined;
    let stopped = false; let rec = null; let stream = null;
    const cycle = () => {
      if (stopped) return;
      const track = getMicTrack && getMicTrack();
      if (!track) { setTimeout(cycle, 3000); return; }
      try { stream = new MediaStream([track.clone()]); } catch (e) { return; }
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
      const parts = []; const startedAt = Date.now();
      try { rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined); } catch (e) { return; }
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size) parts.push(ev.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(parts, { type: rec.mimeType || 'audio/webm' });
        if (blob.size > 2000) {
          const fd = new FormData(); fd.append('file', blob, `chunk.${(rec.mimeType || 'audio/webm').includes('mp4') ? 'm4a' : 'webm'}`); fd.append('started_at', String(startedAt));
          fetch(`${API}/api/calls/${callId}/audio-chunk`, { method: 'POST', headers: hdr, body: fd, keepalive: true }).catch(() => {});
        }
        if (!stopped) cycle();
      };
      rec.start();
      recRef.current = rec;
      setTimeout(() => { if (rec.state === 'recording') rec.stop(); }, CHUNK_MS);
    };
    cycle();
    return () => { stopped = true; try { if (rec && rec.state === 'recording') rec.stop(); } catch (e) { /* noop */ } };
  }, [enabled, live, callId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = (v) => {
    setText(v); typingRef.current = Date.now();
    clearTimeout(save.t);
    save.t = setTimeout(() => fetch(`${API}/api/calls/${callId}/notes`, { method: 'PUT', headers: { ...hdr, 'Content-Type': 'application/json' }, body: JSON.stringify({ text: v }) }).catch(() => {}), 800);
  };
  const toggle = async () => {
    try {
      const r = await fetch(`${API}/api/calls/${callId}/transcription`, { method: 'POST', headers: { ...hdr, 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: !enabled }) });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed');
      setEnabled(!enabled); toast.success(!enabled ? 'AI notes on — everyone is told' : 'AI notes off');
    } catch (e) { toast.error(e.message); }
  };

  if (!open) return null;
  return (
    <div className="absolute inset-x-2 bottom-28 sm:inset-x-auto sm:right-3 sm:w-96 top-16 z-30 rounded-xl bg-gray-900/95 backdrop-blur border border-white/10 text-white flex flex-col shadow-2xl" data-testid="meeting-notes-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="flex items-center gap-2 text-sm font-medium"><NotebookPen className="w-4 h-4" /> Meeting notes</span>
        <div className="flex items-center gap-1">
          {isHost && (
            <Button size="sm" variant="ghost" onClick={toggle} className={`h-7 px-2 text-xs ${enabled ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-white/15 hover:bg-white/25'}`} data-testid="ai-notes-toggle">
              <Sparkles className="w-3.5 h-3.5 mr-1" /> AI notes {enabled ? 'ON' : 'OFF'}
            </Button>
          )}
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose} data-testid="meeting-notes-close"><X className="w-4 h-4" /></Button>
        </div>
      </div>
      <p className="px-3 pt-2 text-[11px] text-gray-400" data-testid="ai-notes-status">
        {enabled ? '● AI notes recording — the summary and transcript are posted to this chat when the call ends.' : isHost ? 'Turn on AI notes to transcribe and summarise this call for everyone.' : 'Only the host can turn AI notes on.'}
      </p>
      <textarea value={text} onChange={(e) => save(e.target.value)} placeholder="Shared notes — everyone in the call can type here…" className="flex-1 m-3 mt-2 min-h-[160px] rounded-lg bg-black/40 border border-white/10 p-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400" data-testid="meeting-notes-text" />
      {meta.by && <p className="px-3 pb-2 text-[10px] text-gray-500">Last edit: {meta.by}</p>}
    </div>
  );
}
